import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { showDefaultErrorToast, translationsToRequestValues } from '@app/utils';
import {
  useCreateQuestionOption,
  useEditQuestionOption,
  useRemoveQuestionOption,
} from '@data/hooks';
import { Language } from '@data/sdk/match';
import { TranslationType, useQuestionOptionsStore } from '@data/storage';
import { APIError } from '@data/utils/types';

type QuestionOptionsParams = {
  defaultTranslations?: TranslationType;
  onAddOption?: () => void;
};

export const useQuestionOptions = ({ defaultTranslations, onAddOption }: QuestionOptionsParams) => {
  const { questionId = '' } = useParams<{ questionId: string }>();

  const [currentLanguage, setCurrentLanguage] = useState<Language>(Language.En);
  const { error, optionToEdit, setOptionToEdit, newOption, setNewOption, resetStore } =
    useQuestionOptionsStore();

  useEffect(() => {
    if (defaultTranslations) setNewOption({ translations: defaultTranslations });
  }, [defaultTranslations]);

  const { mutate: editOption, isPending: optionIsLoading } = useEditQuestionOption();
  const { mutate: createOption, isPending: createIsLoading } = useCreateQuestionOption();
  const { mutate: removeOption } = useRemoveQuestionOption();

  const onError = (err: APIError) => {
    resetStore();
    showDefaultErrorToast(err);
  };

  const handleEditOption = () => {
    if (optionToEdit)
      editOption(
        {
          optionId: optionToEdit?.id,
          values: { translations: translationsToRequestValues(optionToEdit?.translations) },
          questionId,
        },
        { onSuccess: resetStore, onError },
      );
  };

  const handleCreateOption = async () => {
    if (onAddOption) {
      onAddOption();
    } else if (newOption) {
      createOption(
        {
          values: {
            translations: translationsToRequestValues(newOption?.translations),
            key: newOption?.translations[Language.En],
          },
          questionId,
        },
        { onSuccess: resetStore, onError },
      );
    }
  };

  const handleRemoveOption = (optionId: string) => {
    removeOption({ optionId, questionId }, { onSuccess: resetStore, onError });
  };

  return {
    currentLanguage,
    setCurrentLanguage,
    error,
    optionToEdit,
    setOptionToEdit,
    newOption,
    setNewOption,
    handleEditOption,
    handleCreateOption,
    handleRemoveOption,
    optionIsLoading,
    createIsLoading,
  };
};
